import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import { Card, Container, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import * as corsiApi from "../../api/corsiApi";
import * as privateNewApi from "../../api/privateNewApi";
import * as appActions from "../../redux/actions/appActions";
import * as cartActions from "../../redux/actions/cartActions";
import SchedaCorsoCard from "../collettive/SchedaCorsoCard";
import SchedaOffertaCard from "./SchedaOffertaCard";
import RedirectWithTrack from "../common/RedirectWithTrack";
import DettaglioOfferta from "./DettaglioOfferta";
import TestataPagina from "../common/TestataPagina";
import {
  groupPrivateOffers,
  selectVisibleOffers,
  courseRunsNearSelectedDays,
} from "../../utils/privateOffersUtils";

/**
 * Sezione "Offerte / Pacchetti" in fondo all'area lezioni private.
 *
 * Montata sia da PrivateMain sia da PrivateNewMain; renderizza il proprio Container, così le due
 * varianti restano identiche a livello di layout. Se la scuola non ha offerte non renderizza nulla.
 *
 * Le sessioni vengono caricate in stato locale e NON tramite corsiActions.loadSessioni: quello
 * scrive su `state.corsi.sessioni`, uno slot globale condiviso col flusso di dettaglio dei corsi
 * collettivi, che verrebbe sovrascritto.
 *
 * Le offerte su lezione privata (ListiniOfferta, tabella diversa dai CorsoPrivato sopra) si
 * caricano e mostrano SOLO quando `onOfferSelected` è passato: PrivateMain non lo passa e non
 * cambia comportamento, PrivateNewMain sì. Cliccare una card richiama `onOfferSelected` con
 * l'offerta scelta, così chi monta questo componente decide come applicarla (per PrivateNewMain,
 * preselezionando giorno/orario/persone nel suo calendario già esistente).
 *
 * Il fetch parte appena montate (dato piccolo, non dipende dai filtri), ma la VISIBILITÀ delle
 * card offerta è condizionata da `isPreferenzeValid` (niente prezzo/orario senza sapere per cosa)
 * e poi da `selectVisibleOffers`, la stessa regola del nuovo portale: numero di persone coincidente
 * e OGNI giorno selezionato dentro la validità dell'offerta. Le card dei pacchetti CorsoPrivato
 * sopra seguono solo il gate sulle preferenze, non le date.
 */
function OffersSection({
  appData,
  datiScuola,
  promozioniNew,
  cart,
  setCorsoDettagli,
  setPrefsCorso,
  setReferrer,
  onOfferSelected,
  isPreferenzeValid,
  numeroAllieviSelezionati,
  selectedDates,
}) {
  const [offerte, setOfferte] = useState([]);
  const [idCorsoSelezionato, setIdCorsoSelezionato] = useState(null);
  const [corsoSelezionato, setCorsoSelezionato] = useState(null);
  const [sessioniCorso, setSessioniCorso] = useState(null);
  const [redirectToAllievi, setRedirectToAllievi] = useState(false);
  const [offerteListino, setOfferteListino] = useState([]);

  const { t, i18n } = useTranslation([datiScuola?.customTranslationNamespace]);

  useEffect(() => {
    i18n.emit("languageChanged");
  }, [datiScuola?.customTranslationNamespace]);

  useEffect(() => {
    if (!datiScuola?.id) return;

    let cancelled = false;
    (async () => {
      const res = await corsiApi.getListaOfferte(
        datiScuola.id,
        moment().format("YYYY-MM-DD"),
        datiScuola.idLinguaInUso,
        appData?.agenzia?.idAgenzia ?? 0
      );
      if (!cancelled) setOfferte(Array.isArray(res) ? res : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [datiScuola?.id, datiScuola?.idLinguaInUso, appData?.agenzia?.idAgenzia]);

  // Solo per PrivateNewMain: PrivateMain non passa onOfferSelected, quindi non fa questa
  // chiamata e resta esattamente come prima.
  useEffect(() => {
    if (!onOfferSelected) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await privateNewApi.getOffertePrivate(
          appData?.agenzia?.idAgenzia ?? 0
        );
        if (!cancelled) setOfferteListino(groupPrivateOffers(res));
      } catch (err) {
        if (!cancelled) setOfferteListino([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [onOfferSelected, appData?.agenzia?.idAgenzia]);

  async function handleCorsoSelezionato(idCorso) {
    if (idCorsoSelezionato === idCorso) {
      setIdCorsoSelezionato(null);
      setCorsoSelezionato(null);
      setSessioniCorso(null);
      return;
    }

    const corso = offerte.find((x) => x.idCorso === idCorso);
    if (!corso) return;

    setIdCorsoSelezionato(idCorso);
    setCorsoSelezionato(corso);
    setSessioniCorso(null);

    const res = await corsiApi.getListaSessioni(
      datiScuola.id,
      corso.id,
      moment().format("YYYY-MM-DD"),
      datiScuola.idLinguaInUso,
      appData?.agenzia?.idAgenzia ?? -1
    );
    setSessioniCorso(res);
  }

  function handleAvanti(
    primaData,
    giorni,
    numSessioni,
    allievi,
    prezzo,
    prezzoPag,
    prezzoListino,
    listaDate,
    scontoPromozione,
    scontoAgenzia,
    prezzoListinoRiferimento
  ) {
    if (cart.righeCollettive.find((x) => x.roomingList === true) !== undefined) {
      alert(
        "Nel carrello è già presente un corso con rooming list, non è possibile aggiungere un altro corso"
      );
      return;
    }

    setCorsoDettagli(corsoSelezionato);
    setPrefsCorso({
      primaData: primaData,
      numGiorni: giorni,
      numSessioni: numSessioni,
      numAllievi: allievi,
      prezzo: prezzo,
      prezzoPag: prezzoPag,
      prezzoListino: prezzoListino,
      prezzoListinoRiferimento: prezzoListinoRiferimento,
      listaDate: listaDate,
      scontoPromozione: scontoPromozione || 0,
      scontoAgenzia: scontoAgenzia || 0,
      // Il prezzo di un pacchetto è già il totale della comitiva.
      prezzoTotale: true,
      origine: "private",
    });
    setReferrer("private");
    setRedirectToAllievi(true);
  }

  // In PrivateNewMain (onOfferSelected presente) l'intera sezione, header compreso, resta nascosta
  // finché le preferenze non sono valide: mostrare pacchetti/offerte prima non ha senso, non si sa
  // ancora per cosa. In PrivateMain (onOfferSelected assente) non esiste questo concetto ed è
  // esattamente il comportamento di sempre: i pacchetti CorsoPrivato si vedono appena caricati.
  const isPrivateNew = Boolean(onOfferSelected);
  const preferenzeReady = !isPrivateNew || isPreferenzeValid;

  // Anche i pacchetti CorsoPrivato seguono i giorni scelti, come nel nuovo portale: senza giorni
  // non si mostrano, e si mostrano solo se il corso gira vicino ad almeno uno di quelli scelti.
  const corsiVisibili = !preferenzeReady
    ? []
    : isPrivateNew
      ? offerte.filter((corso) =>
          courseRunsNearSelectedDays(corso, selectedDates)
        )
      : offerte;
  const offerteListinoVisibili =
    isPrivateNew && isPreferenzeValid
      ? selectVisibleOffers(
          offerteListino,
          selectedDates,
          numeroAllieviSelezionati
        )
      : [];

  if (corsiVisibili.length === 0 && offerteListinoVisibili.length === 0) {
    return null;
  }

  return (
    <Container>
      {redirectToAllievi && (
        <RedirectWithTrack
          to={`/${appData.lang}/${appData.slug}/collettive/allievi`}
          push
        />
      )}
      <Segment basic style={{ marginBottom: "30px", padding: "0" }}>
        <TestataPagina titolo={t("offerte_pacchetti")} icon="gift" />

        <Card.Group centered style={{ marginTop: "20px" }}>
          {corsiVisibili.map((corso) => (
            <SchedaCorsoCard
              key={corso.idCorso}
              t={t}
              codScuola={datiScuola.codicePrimario || datiScuola.codiceScuola}
              corso={corso}
              isVisible={true}
              onSelect={handleCorsoSelezionato}
            />
          ))}
          {offerteListinoVisibili.map((offerta) => (
            <SchedaOffertaCard
              key={offerta.key}
              t={t}
              offerta={offerta}
              valuta={datiScuola.valuta}
              isVisible={true}
              onSelect={onOfferSelected}
            />
          ))}
        </Card.Group>

        {corsoSelezionato && sessioniCorso && (
          <DettaglioOfferta
            corso={corsoSelezionato}
            sessioniCorso={sessioniCorso}
            datiScuola={datiScuola}
            promozioniNew={promozioniNew}
            appData={appData}
            onAvantiClick={handleAvanti}
          />
        )}
      </Segment>
    </Container>
  );
}

OffersSection.propTypes = {
  appData: PropTypes.object.isRequired,
  datiScuola: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
  setCorsoDettagli: PropTypes.func.isRequired,
  setPrefsCorso: PropTypes.func.isRequired,
  setReferrer: PropTypes.func.isRequired,
  onOfferSelected: PropTypes.func,
  isPreferenzeValid: PropTypes.bool,
  numeroAllieviSelezionati: PropTypes.number,
  selectedDates: PropTypes.array,
};

// datiScuola arriva da scuolaReducer, NON da scuolaNewReducer: PrivateNewMain mappa quest'ultimo,
// quindi la sezione non può ereditarlo come prop.
function mapStateToProps(state) {
  return {
    appData: state.appReducer,
    datiScuola: state.scuolaReducer,
    promozioniNew: state.promozioniNewReducer,
    cart: state.cartReducer,
  };
}

const mapDispatchToProps = {
  setCorsoDettagli: appActions.setCorsoDettagli,
  setPrefsCorso: appActions.setPrefsCorso,
  setReferrer: cartActions.setReferrer,
};

export default connect(mapStateToProps, mapDispatchToProps)(OffersSection);
